import { useState, useEffect } from 'react';
import * as React from 'react';
import { supabase, Card, codeTypes, getCardCode, getCardDescription, hasSeparatedCodes, getHtmlCode, getCssCode, getJsCode, getHtmlInstructions, getCssInstructions, getJsInstructions } from '../../../lib/supabase';
import { categories as defaultCategories } from '../../../mocks/categories';
import { useAuth } from '../../../hooks/useAuth';
import { useAdminContext } from '../../../contexts/AdminContext';
import { getUserEmail, incrementCodeCopyCount } from '../../../lib/admin';
import { useToast } from '../../../components/ui/toast-provider';
import { CardContainer, CardBody } from '../../../components/ui/3d-card';
import { Button } from '../../../components/ui/button';
import { IconButton } from '../../../components/ui/icon-button';
import { CodeTabs } from '../../../components/ui/code-tabs';
import { CodeAccordion } from '../../../components/ui/code-accordion';
import { Skeleton } from '../../../components/ui/skeleton';
import { motion } from 'motion/react';

interface CardItemProps {
  card: Card;
  onCardDeleted: (deletedCardId: string) => void;
  onCardUpdated: (updatedCard: Card) => void;
}

// Componente para o conteúdo do modal de preview
function PreviewModalContent({ 
  card, 
  fullCard, 
  loadingData, 
  onLoadData, 
  onCopyCode, 
  copying, 
  onClose 
}: {
  card: Card;
  fullCard: Card | null;
  loadingData: boolean;
  onLoadData: () => Promise<Card>;
  onCopyCode: (code: string, type: 'html' | 'css' | 'js' | 'code') => void;
  copying: boolean;
  onClose: () => void;
}) {
  const [localFullCard, setLocalFullCard] = React.useState<Card | null>(fullCard);
  const [localLoadingData, setLocalLoadingData] = React.useState(loadingData);

  // Carregar dados automaticamente quando o modal abrir
  React.useEffect(() => {
    if (!localFullCard || !localFullCard.data) {
      setLocalLoadingData(true);
      onLoadData().then((loadedCard) => {
        setLocalFullCard(loadedCard);
        setLocalLoadingData(false);
      }).catch((error) => {
        console.error('Erro ao carregar dados do card:', error);
        setLocalLoadingData(false);
      });
    }
  }, [localFullCard, onLoadData]);

  // Função para detectar se a URL é um vídeo
  const isVideoUrl = (url: string | null | undefined): boolean => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    // Detectar por extensão .mp4 (mesmo com query parameters)
    return urlLower.includes('.mp4') || urlLower.includes('video/mp4');
  };

  return (
    <>
      <div className="p-6 overflow-y-auto" style={{maxHeight: 'calc(85vh - 180px)'}}>
        {card.thumbnail && (
          <div className="mb-6">
            {isVideoUrl(card.thumbnail) ? (
              <video
              src={card.thumbnail} 
              className="w-full h-auto rounded-lg shadow-lg object-contain"
              style={{ maxHeight: '70vh' }}
                controls
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img 
                src={card.thumbnail} 
                alt={card.title}
                className="w-full h-full object-cover rounded-t-xl"
            />
            )}
          </div>
        )}
        
        {!card.thumbnail && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <i className="ri-image-line text-6xl text-gray-400 dark:text-[#c780ff] mb-4"></i>
              <p className="text-gray-600 dark:text-[#c780ff]">Nenhuma miniatura disponível</p>
            </div>
          </div>
        )}
        
        {/* Mostrar descrição e código apenas se já estiverem carregados */}
        {localLoadingData ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton variant="text" width="100%" height="20px" />
              <Skeleton variant="text" width="80%" height="20px" />
              <Skeleton variant="text" width="90%" height="20px" />
            </div>
            <Skeleton variant="rectangular" height={200} className="rounded-lg" />
            <div className="space-y-2">
              <Skeleton variant="text" width="100%" height="16px" />
              <Skeleton variant="text" width="95%" height="16px" />
              <Skeleton variant="text" width="85%" height="16px" />
            </div>
          </div>
        ) : (
          <>
            {localFullCard && localFullCard.data && getCardDescription(localFullCard.data) && (
          <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-[#e0e1dd] mb-2 font-ubuntu">Descrição</h3>
                <p className="text-gray-600 dark:text-[#e0e1dd]/80 font-montserrat">{getCardDescription(localFullCard.data)}</p>
          </div>
        )}
        
            {localFullCard && localFullCard.data ? (
          <div className="mb-6">
                <CodeTabs 
                  card={localFullCard} 
                  onCopy={onCopyCode}
                  copying={copying}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p className="font-montserrat">Nenhum código disponível</p>
          </div>
            )}
          </>
        )}
      </div>
      
      <div className="p-6 border-t border-gray-200 dark:border-[#778da9]/30 flex justify-end space-x-3">
        <Button
          onClick={onClose}
          type="secondary"
          size="medium"
        >
          Fechar
        </Button>
        {/* Só mostrar botão de copiar se for JSON sem códigos separados */}
        {card.code_type === 'json' && !loadingData && (
          <Button
            onClick={() => {
              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:151',message:'Botão Copiar Código no modal clicado',data:{hasFullCard:!!localFullCard,hasData:!!localFullCard?.data,copying,loadingData},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H8',runId:'post-fix'})}).catch(()=>{});
              // #endregion
              onCopyCode();
            }}
            disabled={copying || loadingData}
            loading={copying || loadingData}
            type="primary"
            size="medium"
            prefix={!copying && !loadingData ? <i className="ri-file-copy-line"></i> : undefined}
          >
            {copying ? 'Copiando...' : loadingData ? 'Carregando...' : 'Copiar Código'}
          </Button>
        )}
        {/* Para cards com códigos separados, mostrar mensagem informativa */}
        {card.code_type !== 'json' && !loadingData && (
          <p className="text-sm text-gray-600 dark:text-[#c780ff] italic flex items-center">
            <i className="ri-information-line mr-1"></i>
            Use os botões de cópia em cada aba
          </p>
        )}
      </div>
    </>
  );
}

export default function CardItem({ card, onCardDeleted, onCardUpdated }: CardItemProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(card.title);
  const [editCategories, setEditCategories] = useState<string[]>(
    card.categories || [card.category]
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [copying, setCopying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const closeActionsTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  // Estado para armazenar o card completo (com data) quando carregado sob demanda
  const [fullCard, setFullCard] = useState<Card | null>(card.data ? card : null);
  const [editData, setEditData] = useState({
    title: card.title,
    code: '',
    // Novos campos para HTML/CSS/JS separados
    html_code: '',
    css_code: '',
    js_code: '',
    html_instructions: '',
    css_instructions: '',
    js_instructions: '',
    thumbnail: card.thumbnail || ''
  });
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const toast = useToast();
  const [creatorEmail, setCreatorEmail] = useState<string | null>(null);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const { user } = useAuth();
  const { isAdmin } = useAdminContext();
  
  // Verificar se o card pertence ao usuário atual ou se o usuário é admin
  const isOwner = user && card.user_id === user.id;
  const canEdit = isOwner || isAdmin; // Admin pode editar qualquer card

  // Carregar categorias disponíveis
  useEffect(() => {
    const loadCategories = async () => {
      try {
        // Começar com a lista padrão do mock
        const categorySet = new Set<string>(defaultCategories);
        
        // Buscar apenas campos de categoria do banco de dados (sem o campo data pesado)
        const { data, error } = await supabase
          .from('cards')
          .select('categories, category');
        
        if (!error && data) {
          data.forEach(card => {
            // Adicionar categorias do campo categories (array)
            if (card.categories && Array.isArray(card.categories)) {
              card.categories.forEach(cat => {
                if (cat && cat.trim()) {
                  categorySet.add(cat.trim());
                }
              });
            }
            // Adicionar categoria do campo category (string)
            if (card.category && card.category.trim()) {
              categorySet.add(card.category.trim());
            }
          });
        }
        
        // Converter para array e ordenar
        const categoriesArray = Array.from(categorySet).sort();
        setAvailableCategories(categoriesArray);
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        // Em caso de erro, usar apenas a lista padrão
        setAvailableCategories([...defaultCategories].sort());
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Carregar email do criador do card
  useEffect(() => {
    const loadCreatorEmail = async () => {
      if (card.user_id) {
        const email = await getUserEmail(card.user_id);
        setCreatorEmail(email);
      }
    };
    loadCreatorEmail();
  }, [card.user_id]);

  // Função para abreviar email
  const abbreviateEmail = (email: string | null): string => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 10) {
      return email;
    }
    return `${localPart.substring(0, 10)}...`;
  };

  // Função para detectar se a URL é um vídeo
  const isVideoUrl = (url: string | null | undefined): boolean => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    // Detectar por extensão .mp4 (mesmo com query parameters)
    return urlLower.includes('.mp4') || urlLower.includes('video/mp4');
  };

  // Função para carregar o campo data completo sob demanda
  const loadFullCardData = async (): Promise<Card> => {
    // Se já temos o card completo, retornar
    if (fullCard && fullCard.data) {
      return fullCard;
    }

    // Se já está carregando, aguardar
    if (loadingData) {
      return card;
    }

    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('cards')
        .select('data')
        .eq('id', card.id)
        .single();

      if (error) {
        console.error('Erro ao carregar data do card:', error);
        return card;
      }

      const cardWithData: Card = {
        ...card,
        data: data.data
      };
      setFullCard(cardWithData);
      return cardWithData;
    } catch (error) {
      console.error('Erro ao carregar data do card:', error);
      return card;
    } finally {
      setLoadingData(false);
    }
  };

  const handleDelete = async () => {
    // Limpar timeout se existir
    if (closeActionsTimeoutRef.current) {
      clearTimeout(closeActionsTimeoutRef.current);
      closeActionsTimeoutRef.current = null;
    }
    
    if (!confirm('Tem certeza que deseja excluir este card?')) return;
    
    setIsDeleting(true);
    setDeleting(true);
    try {
      const { data, error } = await supabase
        .from('cards')
        .delete()
        .eq('id', card.id)
        .select();

      if (error) {
        console.error('Erro ao deletar card:', error);
        console.error('Detalhes do erro:', JSON.stringify(error, null, 2));
        alert(`Erro ao deletar card: ${error.message || 'Erro desconhecido'}`);
        setIsDeleting(false);
        setDeleting(false);
        return;
      }

      // Verificar se realmente deletou
      if (data && data.length > 0) {
        console.log('Card deletado com sucesso:', data);
        onCardDeleted(card.id);
      } else {
        console.warn('Nenhum card foi deletado. Verifique as permissões RLS.');
        alert('Não foi possível deletar o card. Verifique suas permissões.');
      }
    } catch (error: any) {
      console.error('Erro inesperado:', error);
      alert(`Erro inesperado ao deletar card: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setIsDeleting(false);
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editData.title.trim()) {
      alert('O título não pode estar vazio');
      return;
    }

    // Validação: se não for JSON, pelo menos um código deve estar preenchido
    if (card.code_type !== 'json') {
      const hasAnyCode = editData.html_code.trim() || editData.css_code.trim() || editData.js_code.trim();
      if (!hasAnyCode) {
        alert('Pelo menos um dos códigos (HTML, CSS ou JS) deve estar preenchido.');
        return;
      }
    } else {
      // Para JSON, o campo code deve estar preenchido
      if (!editData.code.trim()) {
        alert('O código JSON não pode estar vazio');
        return;
      }
    }

    setIsSaving(true);
    setSaving(true);
    try {
      // Preparar o objeto data baseado no tipo de código
      let cardData: any = {};

      if (card.code_type === 'json') {
        // Para JSON, usar campo único code
      let processedCode: string | any = editData.code;
      
      if (typeof processedCode === 'string' && processedCode.trim()) {
        try {
          // Tentar fazer parse do JSON
          const parsedCode = JSON.parse(processedCode);
          
          // Se for um objeto e tiver a estrutura do Elementor, usar o objeto parseado
          if (parsedCode && typeof parsedCode === 'object' && parsedCode.type === 'elementor') {
              processedCode = parsedCode;
            console.log('✅ Código Elementor detectado, salvando como objeto JSONB');
          }
          // Se não for Elementor mas for JSON válido, manter como objeto também
          else if (parsedCode && typeof parsedCode === 'object') {
            processedCode = parsedCode;
            console.log('✅ JSON válido detectado, salvando como objeto JSONB');
          }
        } catch (e) {
          // Não é JSON válido, manter como string
          console.log('ℹ️ Código não é JSON válido, salvando como string');
        }
        }
        cardData.code = processedCode;
      } else {
        // Para HTML/CSS/JS, usar campos separados
        if (editData.html_code.trim()) {
          cardData.html_code = editData.html_code.trim();
        }
        if (editData.css_code.trim()) {
          cardData.css_code = editData.css_code.trim();
        }
        if (editData.js_code.trim()) {
          cardData.js_code = editData.js_code.trim();
        }
        if (editData.html_instructions.trim()) {
          cardData.html_instructions = editData.html_instructions.trim();
        }
        if (editData.css_instructions.trim()) {
          cardData.css_instructions = editData.css_instructions.trim();
        }
        if (editData.js_instructions.trim()) {
          cardData.js_instructions = editData.js_instructions.trim();
        }
      }

      // Preservar outros campos do data (como description)
      const currentCard = fullCard || card;
      if (currentCard.data) {
        if (currentCard.data.description) {
          cardData.description = currentCard.data.description;
        }
        // Preservar outros campos que possam existir
        Object.keys(currentCard.data).forEach(key => {
          if (!['code', 'html_code', 'css_code', 'js_code', 'html_instructions', 'css_instructions', 'js_instructions'].includes(key)) {
            cardData[key] = currentCard.data[key];
          }
        });
      }

      const { error } = await supabase
        .from('cards')
        .update({
          title: editData.title,
          thumbnail: editData.thumbnail,
          data: cardData,
          categories: editCategories,
          category: editCategories[0] || 'Geral'
        })
        .eq('id', card.id);

      if (error) {
        console.error('Erro ao atualizar card:', error);
        alert('Erro ao atualizar card');
      } else {
        setIsEditing(false);
        setShowEditModal(false);
        // Retornar o card atualizado para atualização otimista
        const currentCard = fullCard || card;
        const updatedCard: Card = {
          ...currentCard,
          title: editData.title,
          thumbnail: editData.thumbnail,
          data: cardData, // Usar o cardData que foi salvo
          categories: editCategories,
          category: editCategories[0] || 'Geral'
        };
        setFullCard(updatedCard);
        onCardUpdated(updatedCard);
      }
    } catch (error) {
      console.error('Erro inesperado:', error);
      alert('Erro inesperado ao atualizar card');
    } finally {
      setIsSaving(false);
      setSaving(false);
    }
  };

  const handleCategoryToggle = (category: string) => {
    setEditCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleAddCategory = () => {
    if (newCategoryName.trim() && !availableCategories.includes(newCategoryName.trim())) {
      const newCategory = newCategoryName.trim();
      
      // Adicionar à lista local imediatamente
      setAvailableCategories(prev => [...prev, newCategory].sort());
      
      // Adicionar às categorias selecionadas
      setEditCategories(prev => [...prev, newCategory]);
      
      setNewCategoryName('');
      setShowNewCategoryModal(false);
    }
  };

  const handleCopyCode = async (code?: string, type?: 'html' | 'css' | 'js' | 'code') => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:515',message:'handleCopyCode iniciado',data:{hasCode:!!code,codeLength:code?.length,type,userId:user?.id,cardId:card.id,cardCodeType:card.code_type},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2,H8',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    console.log('handleCopyCode chamado:', { code: code?.substring(0, 50), type, cardCodeType: card.code_type });
    
    // Se o código já foi passado, usar diretamente (vem do CodeTabs no modal)
    if (code && code.trim() !== '') {
    setCopying(true);
    try {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:522',message:'Código passado como parâmetro (do CodeTabs)',data:{userId:user?.id,hasUser:!!user,type:type},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H9',runId:'post-fix'})}).catch(()=>{});
        // #endregion
        toast.success('Código copiado para a área de transferência!');
        setCopying(false);
        
        // NÃO incrementar aqui pois o CodeTabs já incrementou
        // O código chegou aqui via onCopy callback, então já foi contabilizado
      } catch (error) {
        console.error('Erro ao copiar:', error);
        toast.error('Erro ao copiar código');
        setCopying(false);
      }
      return;
    }
    
    // Se não foi passado código, verificar se é JSON ou não
    // Se não for JSON, sempre mostrar toast informativo (mesmo que ainda não tenha campos separados)
    if (card.code_type && card.code_type !== 'json') {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:559',message:'Card não é JSON, mostrando toast',data:{codeType:card.code_type},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H7',runId:'post-fix'})}).catch(()=>{});
      // #endregion
      console.log('Card não é JSON, mostrando toast informativo. code_type:', card.code_type);
      setCopying(false);
      toast.info('Clique em "Visualizar" para copiar o código correspondente (HTML, CSS ou JS)');
      return;
    }
    
    // Se for JSON ou code_type não definido, verificar se tem códigos separados
    setCopying(true);
    try {
      const cardWithData = await loadFullCardData();
      const hasSeparated = hasSeparatedCodes(cardWithData.data);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:572',message:'Verificou códigos separados',data:{hasSeparated:hasSeparated,codeType:card.code_type},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H7',runId:'post-fix'})}).catch(()=>{});
      // #endregion
      
      // Se tem códigos separados, mostrar toast informativo
      if (hasSeparated) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:574',message:'Tem códigos separados, mostrando toast',data:{},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H7',runId:'post-fix'})}).catch(()=>{});
        // #endregion
        setCopying(false);
        toast.info('Clique em "Visualizar" para copiar o código correspondente (HTML, CSS ou JS)');
        return;
      }
      
      // Para JSON sem códigos separados, copiar normalmente
      const codeToCopy = getCardCode(cardWithData.data);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:580',message:'Código extraído para cópia',data:{hasCode:!!codeToCopy,codeLength:codeToCopy?.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H8',runId:'post-fix'})}).catch(()=>{});
      // #endregion
      if (!codeToCopy || codeToCopy.trim() === '') {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:582',message:'Nenhum código disponível',data:{},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H8',runId:'post-fix'})}).catch(()=>{});
        // #endregion
        setCopying(false);
        toast.error('Nenhum código disponível para copiar');
        return;
      }
      
      await navigator.clipboard.writeText(codeToCopy);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:587',message:'Código copiado para clipboard (modal)',data:{codeLength:codeToCopy.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H8',runId:'post-fix'})}).catch(()=>{});
      // #endregion
      
      // Mostrar toast de sucesso
      toast.success('Código copiado para a área de transferência!');
      setCopying(false);
      
      // Rastrear cópia de forma assíncrona e não bloqueante
      if (user?.id) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:578',message:'Chamando incrementCodeCopyCount (segundo local)',data:{userId:user.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2,H3'})}).catch(()=>{});
        // #endregion
        incrementCodeCopyCount(user.id).catch(err => {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:579',message:'Erro capturado no catch (segundo local)',data:{error:err?.message,errorStack:err?.stack},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H3'})}).catch(()=>{});
          // #endregion
          console.error('Erro ao rastrear cópia:', err);
          // Erro silencioso - não afetar UX
        });
      } else {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CardItem.tsx:583',message:'user.id não existe (segundo local)',data:{user:user},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2'})}).catch(()=>{});
        // #endregion
      }
    } catch (error) {
      console.error('Erro ao copiar:', error);
      toast.error('Erro ao copiar código');
      setCopying(false);
    }
  };

  const handleEdit = async () => {
    // Limpar timeout se existir
    if (closeActionsTimeoutRef.current) {
      clearTimeout(closeActionsTimeoutRef.current);
      closeActionsTimeoutRef.current = null;
    }
    
    // Carregar o campo data completo apenas quando necessário
    const cardWithData = await loadFullCardData();
    const hasSeparated = hasSeparatedCodes(cardWithData.data);
    
    if (hasSeparated) {
      // Se tem códigos separados, carregar os campos separados
      setEditData({
        title: cardWithData.title,
        code: '',
        html_code: getHtmlCode(cardWithData.data) || '',
        css_code: getCssCode(cardWithData.data) || '',
        js_code: getJsCode(cardWithData.data) || '',
        html_instructions: getHtmlInstructions(cardWithData.data) || '',
        css_instructions: getCssInstructions(cardWithData.data) || '',
        js_instructions: getJsInstructions(cardWithData.data) || '',
        thumbnail: cardWithData.thumbnail || ''
      });
    } else {
      // Se não tem códigos separados, usar o campo único (JSON ou formato antigo)
    setEditData({
      title: cardWithData.title,
      code: getCardCode(cardWithData.data),
        html_code: '',
        css_code: '',
        js_code: '',
        html_instructions: '',
        css_instructions: '',
        js_instructions: '',
      thumbnail: cardWithData.thumbnail || ''
    });
    }
    setEditCategories(cardWithData.categories || [cardWithData.category]);
    setShowEditModal(true);
    setShowActions(false);
  };

  const handleViewCode = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowActions(false);
    setShowPreview(true);
    // OTIMIZAÇÃO: NÃO carregar o campo data automaticamente
    // O data só será carregado quando o usuário clicar em "Copiar Código"
    // Isso reduz drasticamente o egress do Supabase
  };

  const getCodeTypeInfo = () => {
    const type = codeTypes.find(t => t.value === card.code_type);
    return type || { value: 'other', label: 'Outro', icon: 'ri-code-line' };
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'css':
        return 'ri-css3-line';
      case 'js':
        return 'ri-javascript-line';
      case 'html':
        return 'ri-html5-line';
      case 'json':
        return 'ri-braces-line';
      default:
        return 'ri-code-line';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'css':
        return 'bg-[#1b263b]/20 text-[#1b263b] dark:bg-[#1b263b]/50 dark:text-white';
      case 'js':
        return 'bg-[#778da9]/20 text-[#778da9] dark:bg-[#778da9]/50 dark:text-white';
      case 'html':
        return 'bg-[#6a0dad]/20 text-[#6a0dad] dark:bg-[#6a0dad]/50 dark:text-white';
      case 'json':
        return 'bg-[#0d1b2a]/20 text-[#0d1b2a] dark:bg-[#0d1b2a]/50 dark:text-white';
      default:
        return 'bg-[#778da9]/20 text-[#778da9] dark:bg-[#778da9]/50 dark:text-white';
    }
  };

  // Função para abrir o menu de ações com delay de fechamento
  const handleOpenActions = () => {
    // Limpar timeout anterior se existir
    if (closeActionsTimeoutRef.current) {
      clearTimeout(closeActionsTimeoutRef.current);
      closeActionsTimeoutRef.current = null;
    }
    setShowActions(true);
  };

  // Função para fechar o menu de ações com delay
  const handleCloseActions = () => {
    // Adicionar delay antes de fechar para dar tempo do usuário mover o mouse
    closeActionsTimeoutRef.current = setTimeout(() => {
      setShowActions(false);
      closeActionsTimeoutRef.current = null;
    }, 200); // 200ms de delay
  };

  // Função para cancelar o fechamento (quando mouse entra no menu)
  const handleCancelClose = () => {
    if (closeActionsTimeoutRef.current) {
      clearTimeout(closeActionsTimeoutRef.current);
      closeActionsTimeoutRef.current = null;
    }
  };

  // Limpar timeout ao desmontar
  React.useEffect(() => {
    return () => {
      if (closeActionsTimeoutRef.current) {
        clearTimeout(closeActionsTimeoutRef.current);
      }
    };
  }, []);

  // Fechar menu ao clicar fora
  React.useEffect(() => {
    if (!showActions) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Verificar se o clique foi fora do menu e do botão
      const isClickInsideMenu = target.closest('[data-actions-menu]');
      const isClickInsideButton = target.closest('[data-actions-button]');
      
      if (!isClickInsideMenu && !isClickInsideButton) {
        setShowActions(false);
        if (closeActionsTimeoutRef.current) {
          clearTimeout(closeActionsTimeoutRef.current);
          closeActionsTimeoutRef.current = null;
    }
      }
    };

    // Adicionar listener após um pequeno delay para não fechar imediatamente ao abrir
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActions]);

  const cardCategories = card.categories || [card.category];
  const codeTypeInfo = getCodeTypeInfo();

  // Debug: verificar se é vídeo
  const thumbnailIsVideo = isVideoUrl(card.thumbnail);
  if (card.thumbnail && thumbnailIsVideo) {
    console.log('🎬 Card com vídeo detectado:', {
      id: card.id,
      title: card.title,
      thumbnail: card.thumbnail,
      isVideo: thumbnailIsVideo
    });
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        data-card-id={card.id}
        className="relative"
      >
        <CardContainer containerClassName="w-full">
          <CardBody className="w-full">
            <div 
              className="relative glass-card rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden group h-full flex flex-col [&_button]:transform-none [&_button]:will-change-auto"
      >
        {/* Miniatura */}
        {card.thumbnail && (
          <div className="relative h-48 flex-shrink-0">
            {isVideoUrl(card.thumbnail) ? (
              <video
                key={card.thumbnail}
                src={card.thumbnail}
                className="w-full h-full object-cover rounded-t-xl"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                controls={false}
                onError={(e) => {
                  const target = e.currentTarget as HTMLVideoElement;
                  const error = target.error;
                  console.error('❌ Erro ao carregar vídeo no card:', card.thumbnail);
                  console.error('Card ID:', card.id);
                  console.error('Erro code:', error?.code);
                  console.error('Erro message:', error?.message);
                  console.error('Network state:', target.networkState);
                  console.error('Ready state:', target.readyState);
                  
                  // Erro code 4 = MEDIA_ERR_SRC_NOT_SUPPORTED
                  // Isso geralmente significa que o arquivo não existe, está corrompido ou formato não suportado
                  if (error?.code === 4) {
                    console.error('   ⚠️ MEDIA_ERR_SRC_NOT_SUPPORTED - Arquivo pode estar corrompido, vazio ou não existe');
                    console.error('   Teste a URL diretamente no navegador:', card.thumbnail);
                    
                    // Verificar se a URL retorna algo
                    fetch(card.thumbnail, { method: 'HEAD' })
                      .then(response => {
                        console.log('   📡 Resposta HEAD:', response.status, response.statusText);
                        console.log('   Content-Type:', response.headers.get('content-type'));
                        console.log('   Content-Length:', response.headers.get('content-length'));
                        if (!response.ok) {
                          console.error('   ❌ Arquivo não encontrado no storage (404)');
                        } else {
                          const contentType = response.headers.get('content-type');
                          if (contentType && !contentType.includes('video')) {
                            console.error('   ❌ Content-Type incorreto:', contentType);
                          }
                        }
                      })
                      .catch(fetchError => {
                        console.error('   ❌ Erro ao verificar URL:', fetchError);
                      });
                  }
                  
                  // Não tentar recarregar automaticamente - pode causar loop infinito
                  target.style.display = 'none';
                }}
                onLoadStart={() => {
                  console.log('▶️ Vídeo iniciando carregamento no card:', card.id);
                  console.log('   URL:', card.thumbnail);
                }}
                onLoadedMetadata={() => {
                  console.log('✅ Metadados do vídeo carregados no card:', card.id);
                  console.log('   Duração:', (e.currentTarget as HTMLVideoElement).duration);
                  console.log('   Dimensões:', (e.currentTarget as HTMLVideoElement).videoWidth, 'x', (e.currentTarget as HTMLVideoElement).videoHeight);
                }}
                onLoadedData={() => {
                  console.log('✅ Vídeo carregado no card:', card.id);
                  const target = e.currentTarget as HTMLVideoElement;
                  target.style.opacity = '1';
                }}
                onCanPlay={() => {
                  console.log('✅ Vídeo pode ser reproduzido no card:', card.id);
                  const target = e.currentTarget as HTMLVideoElement;
                  target.style.opacity = '1';
                }}
                onCanPlayThrough={() => {
                  console.log('✅ Vídeo pode ser reproduzido completamente no card:', card.id);
                  const target = e.currentTarget as HTMLVideoElement;
                  target.style.opacity = '1';
                }}
                onPlay={() => {
                  console.log('▶️ Vídeo iniciou reprodução no card:', card.id);
                  const target = e.currentTarget as HTMLVideoElement;
                  target.style.opacity = '1';
                }}
                onWaiting={() => {
                  console.log('⏳ Vídeo aguardando buffer no card:', card.id);
                }}
                onStalled={() => {
                  console.warn('⚠️ Vídeo travado no card:', card.id);
                }}
              />
            ) : (
            <img 
              src={card.thumbnail} 
              alt={card.title}
                className="w-full h-full object-cover rounded-t-xl"
              onError={(e) => {
                  if (card.thumbnail.startsWith('blob:')) {
                    console.error('❌ Blob URL inválido no card (não funciona após recarregar):', card.thumbnail);
                    console.error('   Este card precisa ser editado e a imagem precisa ser reenviada.');
                e.currentTarget.style.display = 'none';
                    // Mostrar mensagem visual se possível
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'absolute inset-0 flex items-center justify-center bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-xs p-2 text-center';
                    errorDiv.textContent = 'Imagem inválida. Edite o card e reenvie a imagem.';
                    e.currentTarget.parentElement?.appendChild(errorDiv);
                  } else {
                    console.error('❌ Erro ao carregar imagem no card:', card.thumbnail);
                    e.currentTarget.style.display = 'none';
                  }
              }}
            />
            )}
            {/* Badge do criador sobre a imagem/vídeo */}
            {creatorEmail && !isOwner && (
            <div className="absolute top-3 right-3">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-black/40 backdrop-blur-sm text-white border border-white/20 shadow-lg font-montserrat">
                  {abbreviateEmail(creatorEmail)}
                </span>
              </div>
            )}
            {canEdit && (
              <div className="absolute top-3 right-3">
                <IconButton
                  data-actions-button
                  onClick={handleOpenActions}
                  onMouseEnter={handleCancelClose}
                  onMouseLeave={handleCloseActions}
                  variant="ghost"
                  size="sm"
                  className="bg-black/20 backdrop-blur-sm text-white hover:bg-black/30"
              >
                <i className="ri-more-2-line text-lg"></i>
                </IconButton>
            </div>
            )}
          </div>
        )}

        <div className="p-6 flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 font-ubuntu">
                {card.title}
              </h3>
              </div>
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(card.code_type)}`}>
                  <i className={`${getTypeIcon(card.code_type)} mr-1`}></i>
                  {card.code_type?.toUpperCase()}
                </span>
                {cardCategories.length > 0 && cardCategories.map((category, index) => (
                  category && (
                    <span 
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#8a2be2]/20 text-[#6a0dad] dark:bg-[#6a0dad]/40 dark:text-white"
                    >
                      {category}
                    </span>
                  )
                ))}
              </div>
            </div>
            
            {!card.thumbnail && canEdit && (
              <div className="flex items-center space-x-2 ml-4">
                <IconButton
                  data-actions-button
                  onClick={handleOpenActions}
                  onMouseEnter={handleCancelClose}
                  onMouseLeave={handleCloseActions}
                  variant="ghost"
                  size="sm"
                >
                  <i className="ri-more-2-line text-lg"></i>
                </IconButton>
              </div>
            )}
          </div>

          {/* Description */}
          {fullCard && getCardDescription(fullCard.data) && (
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3 font-montserrat flex-1">
              {getCardDescription(fullCard.data)}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center mt-auto relative z-10">
            <div className="flex items-center space-x-3 pointer-events-auto">
              <Button
                buttonType="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Botão Copiar clicado, card.code_type:', card.code_type);
                  handleCopyCode();
                }}
                disabled={copying}
                loading={copying}
                type="primary"
                size="small"
                className="relative z-20 transform-none"
                prefix={<i className={copying ? "" : "ri-file-copy-line"}></i>}
              >
                {copying ? 'Copiando...' : 'Copiar'}
              </Button>
              
              <Button
                buttonType="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleViewCode(e);
                }}
                type="secondary"
                size="small"
                className="relative z-20 transform-none"
                prefix={<i className="ri-eye-line"></i>}
              >
                Visualizar
              </Button>
            </div>
          </div>
        </div>

        {/* Actions Dropdown - Para o dono do card ou admin */}
        {showActions && canEdit && (
          <motion.div
            data-actions-menu
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onMouseEnter={handleCancelClose}
            onMouseLeave={handleCloseActions}
            className="absolute top-16 right-6 glass-card rounded-lg shadow-xl py-2 z-50 min-w-[160px]"
          >
            <Button
              onMouseDown={(e) => {
                e.preventDefault();
                handleEdit();
                setShowActions(false);
              }}
              type="tertiary"
              size="small"
              fullWidth
              className="w-full justify-start text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition-colors duration-150"
              prefix={<i className="ri-edit-line text-[#6a0dad] dark:text-[#c780ff]"></i>}
            >
              Editar
            </Button>
            
            <Button
              onMouseDown={(e) => {
                e.preventDefault();
                handleDelete();
                setShowActions(false);
              }}
              disabled={deleting}
              loading={deleting}
              type="error"
              size="small"
              fullWidth
              className="w-full justify-start transition-colors duration-150"
              prefix={<i className={deleting ? "" : "ri-delete-bin-line"}></i>}
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </motion.div>
        )}
      </div>
          </CardBody>
        </CardContainer>
      </motion.div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1b263b] rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-[#778da9]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                  {card.title}
                </h2>
                <IconButton
                  onClick={() => setShowPreview(false)}
                  variant="ghost"
                  size="md"
                >
                  <i className="ri-close-line text-xl"></i>
                </IconButton>
              </div>
            </div>
            
            <PreviewModalContent 
              card={card}
              fullCard={fullCard}
              loadingData={loadingData}
              onLoadData={loadFullCardData}
              onCopyCode={handleCopyCode}
              copying={copying}
              onClose={() => setShowPreview(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative border border-gray-200 dark:border-[#8a2be2]/30">
            {/* Botão Fechar */}
                <button
                  onClick={() => setShowEditModal(false)}
              className="absolute top-2 right-4 p-2 text-gray-500 dark:text-[#c780ff] hover:text-gray-700 dark:hover:text-[#e0e1dd] hover:bg-gray-100 dark:hover:bg-[#0d0d0d]/50 rounded-lg transition-colors duration-200 z-10"
              aria-label="Fechar modal"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
            
            <div className="p-6 border-b border-gray-200 dark:border-[#8a2be2]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                  Editar Código
                </h2>
              </div>
            </div>
            
            <form onSubmit={handleSaveEdit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({...editData, title: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2">
                  URL da Miniatura ou Cole uma Imagem (Ctrl+V)
                </label>
                <div className="space-y-3">
                <input
                  type="url"
                  value={editData.thumbnail}
                  onChange={(e) => setEditData({...editData, thumbnail: e.target.value})}
                    onPaste={async (e) => {
                      // Detectar se o usuário colou uma imagem
                      const items = e.clipboardData?.items;
                      if (!items) return;

                      for (let i = 0; i < items.length; i++) {
                        const item = items[i];
                        
                        // Verificar se é uma imagem
                        if (item.type.indexOf('image') !== -1) {
                          e.preventDefault();
                          
                          const file = item.getAsFile();
                          if (!file) continue;

                          setIsUploadingVideo(true);
                          try {
                            // Detectar tipo MIME e extensão
                            const mimeType = file.type || 'image/png';
                            const ext = mimeType.split('/')[1] || 'png';
                            const fileName = `cards/${card.id}.${ext}`;
                            
                            // Fazer upload para Supabase Storage
                            const { data: uploadData, error: uploadError } = await supabase.storage
                              .from('thumbnails')
                              .upload(fileName, file, {
                                contentType: mimeType,
                                upsert: true,
                                cacheControl: '3600'
                              });

                            if (uploadError) {
                              throw uploadError;
                            }

                            // Aguardar um pouco para garantir que o arquivo está disponível
                            await new Promise(resolve => setTimeout(resolve, 500));

                            // Obter URL pública com cache buster para forçar reload
                            const timestamp = Date.now();
                            const { data: { publicUrl } } = supabase.storage
                              .from('thumbnails')
                              .getPublicUrl(fileName);

                            // Adicionar timestamp como query param para forçar reload
                            const publicUrlWithCache = `${publicUrl}?t=${timestamp}`;

                            setEditData({...editData, thumbnail: publicUrlWithCache});
                            console.log('✅ Imagem colada e enviada com sucesso:', publicUrlWithCache);
                            setShowToast(true);
                          } catch (error: any) {
                            console.error('Erro ao fazer upload da imagem colada:', error);
                            alert('Erro ao fazer upload da imagem: ' + (error.message || 'Erro desconhecido'));
                          } finally {
                            setIsUploadingVideo(false);
                          }
                          break;
                        }
                      }
                    }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200"
                    placeholder="Cole uma imagem (Ctrl+V) ou cole uma URL aqui"
                  />
                  <div className="text-xs text-gray-500 dark:text-[#778da9] flex items-center space-x-1">
                    <i className="ri-information-line"></i>
                    <span>Dica: Você pode colar uma imagem diretamente (Ctrl+V) ou colar uma URL de imagem</span>
                  </div>
                  {isUploadingVideo && (
                    <div className="flex items-center space-x-2 text-sm text-[#6a0dad] dark:text-[#c780ff]">
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Enviando imagem...</span>
                    </div>
                  )}
                  {/* Upload de vídeo se for URL externa de vídeo */}
                  {editData.thumbnail && editData.thumbnail.toLowerCase().includes('.mp4') && editData.thumbnail.includes('packlp.com') && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                        ⚠️ Vídeo externo detectado. Faça upload do vídeo para melhorar o desempenho:
                      </p>
                      <input
                        type="file"
                        accept="video/mp4"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              alert('O vídeo deve ter no máximo 5MB');
                              return;
                            }
                            setIsUploadingVideo(true);
                            try {
                              const fileExt = file.name.split('.').pop() || 'mp4';
                              const fileName = `cards/${card.id}.${fileExt}`;
                              
                              // Usar API REST diretamente para garantir Content-Type correto
                              const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
                              const { data: { session } } = await supabase.auth.getSession();
                              const token = session?.access_token || supabaseKey;
                              const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://bwrjzklcyhddlpzbjlgd.supabase.co';
                              
                              const uploadUrl = `${SUPABASE_URL}/storage/v1/object/thumbnails/${fileName}`;
                              
                              const arrayBuffer = await file.arrayBuffer();
                              
                              const response = await fetch(uploadUrl, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'video/mp4',
                                  'x-upsert': 'true',
                                  'Cache-Control': '3600',
                                  'apikey': supabaseKey
                                },
                                body: arrayBuffer
                              });

                              if (!response.ok) {
                                const errorText = await response.text();
                                throw new Error(`Upload falhou: ${response.status} - ${errorText}`);
                              }

                              // Obter URL pública
                              const { data: { publicUrl } } = supabase.storage
                                .from('thumbnails')
                                .getPublicUrl(fileName);

                            setEditData({...editData, thumbnail: publicUrl});
                            console.log('✅ Vídeo enviado com sucesso:', publicUrl);
                              setShowToast(true);
                            } catch (error: any) {
                              console.error('Erro ao fazer upload:', error);
                              alert('Erro ao fazer upload do vídeo: ' + (error.message || 'Erro desconhecido'));
                            } finally {
                              setIsUploadingVideo(false);
                            }
                          }
                        }}
                        disabled={isUploadingVideo}
                        className="block w-full text-sm text-gray-500 dark:text-[#c780ff] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#6a0dad] file:text-white hover:file:bg-[#8a2be2] cursor-pointer disabled:opacity-50"
                />
                      {isUploadingVideo && (
                        <p className="text-xs text-gray-600 dark:text-[#778da9] mt-2">
                          <i className="ri-loader-4-line animate-spin mr-1"></i>
                          Enviando vídeo...
                        </p>
                      )}
                    </div>
                  )}
                {editData.thumbnail && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 dark:text-[#c780ff] mb-2">Preview da miniatura:</p>
                      {editData.thumbnail.toLowerCase().includes('.mp4') ? (
                        <video
                          src={editData.thumbnail}
                          className="w-32 h-20 object-cover rounded-lg border border-gray-200 dark:border-[#778da9]/30"
                          autoPlay
                          loop
                          muted
                          playsInline
                          controls={false}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                    <img 
                      src={editData.thumbnail} 
                      alt="Preview" 
                      className="w-32 h-20 object-cover rounded-lg border border-gray-200 dark:border-[#778da9]/30"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                      )}
                  </div>
                )}
                </div>
              </div>

              {/* Categorias */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-3">
                  Categorias (selecione uma ou mais)
                </label>
                
                {loadingCategories ? (
                  <div className="flex items-center justify-center py-8">
                    <i className="ri-loader-4-line text-2xl text-[#6a0dad] animate-spin"></i>
                    <span className="ml-2 text-gray-600 dark:text-[#c780ff]">Carregando categorias...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {availableCategories.map((category) => (
                      <label key={category} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editCategories.includes(category)}
                          onChange={() => handleCategoryToggle(category)}
                          className="w-4 h-4 text-[#6a0dad] bg-gray-100 border-gray-300 rounded focus:ring-[#8a2be2] dark:focus:ring-[#8a2be2] dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-[#e0e1dd]">{category}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                {/* Botão Adicionar Nova Categoria */}
                <div className="mt-4">
                  <Button
                    type="button"
                    onClick={() => setShowNewCategoryModal(true)}
                    type="secondary"
                    size="medium"
                    className="border-2 border-dashed border-[#6a0dad] dark:border-[#8a2be2] text-[#6a0dad] dark:text-[#8a2be2] hover:bg-[#6a0dad]/10 dark:hover:bg-[#8a2be2]/10"
                    prefix={<i className="ri-add-line"></i>}
                  >
                    Adicionar Nova Categoria
                  </Button>
                </div>
                
                {editCategories.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 dark:text-[#c780ff]">
                      Selecionadas: <span className="font-medium">{editCategories.join(', ')}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Código - JSON ou HTML/CSS/JS separados */}
              {card.code_type === 'json' ? (
              <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                    Código JSON
                </label>
                <textarea
                  value={editData.code}
                  onChange={(e) => setEditData({...editData, code: e.target.value})}
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#778da9]/30 rounded-lg focus:ring-2 focus:ring-[#415a77] focus:border-[#415a77] dark:bg-[#0d1b2a]/50 dark:text-[#e0e1dd] font-mono text-sm transition-colors duration-200"
                  required
                    placeholder="Cole seu código JSON aqui..."
                />
              </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-3 font-montserrat">
                    Códigos (HTML, CSS, JavaScript)
                  </label>
                  <CodeAccordion
                    htmlCode={editData.html_code}
                    cssCode={editData.css_code}
                    jsCode={editData.js_code}
                    htmlInstructions={editData.html_instructions}
                    cssInstructions={editData.css_instructions}
                    jsInstructions={editData.js_instructions}
                    onHtmlCodeChange={(value) => setEditData({...editData, html_code: value})}
                    onCssCodeChange={(value) => setEditData({...editData, css_code: value})}
                    onJsCodeChange={(value) => setEditData({...editData, js_code: value})}
                    onHtmlInstructionsChange={(value) => setEditData({...editData, html_instructions: value})}
                    onCssInstructionsChange={(value) => setEditData({...editData, css_instructions: value})}
                    onJsInstructionsChange={(value) => setEditData({...editData, js_instructions: value})}
                  />
                  <p className="mt-3 text-xs text-gray-500 dark:text-[#c780ff] font-montserrat">
                    <i className="ri-information-line mr-1"></i>
                    Pelo menos um dos códigos (HTML, CSS ou JS) deve estar preenchido. Os campos vazios serão exibidos como "Opcional" na visualização.
                  </p>
                </div>
              )}
            </form>
            
            <div className="p-6 border-t border-gray-200 dark:border-[#778da9]/30 flex justify-end space-x-4">
              <Button
                type="button"
                onClick={() => setShowEditModal(false)}
                type="secondary"
                size="medium"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                loading={saving}
                type="primary"
                size="medium"
                prefix={!saving ? <i className="ri-save-line"></i> : undefined}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nova Categoria */}
      {showNewCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-[#1b263b] rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-[#778da9]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                  Nova Categoria
                </h2>
                <IconButton
                  onClick={() => {
                    setShowNewCategoryModal(false);
                    setNewCategoryName('');
                  }}
                  variant="ghost"
                  size="md"
                >
                  <i className="ri-close-line text-xl"></i>
                </IconButton>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Nome da Categoria
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCategory();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#778da9]/30 rounded-lg focus:ring-2 focus:ring-[#415a77] focus:border-[#415a77] dark:bg-[#0d1b2a]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  placeholder="Ex: Animações, Layouts, etc."
                  autoFocus
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  onClick={() => {
                    setShowNewCategoryModal(false);
                    setNewCategoryName('');
                  }}
                  type="secondary"
                  size="medium"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddCategory}
                  type="primary"
                  size="medium"
                >
                  Adicionar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
